import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {pullRequestMarkersKey, type Markers} from '../page-data/loaders/use-markers-data'

export type UpdateReviewCommentMutationResponse = {
  body: string
  bodyHTML: string
  commentDatabaseId: number
  threadId: number
}

/**
 * Updates the body of a review comment
 */
export function useUpdateReviewCommentMutation(basePath: string) {
  const apiUrl = `${basePath}/page_data/${PageData.updateReviewComment}`
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({commentId, bodyVersion, body}: {commentId: string; bodyVersion?: string; body: string}) => {
      const response = await fetchWithErrorHandling(`${apiUrl}${bodyVersion ? `?body_version=${bodyVersion}` : ''}`, {
        method: 'PUT',
        body: {
          body,
          commentId,
        },
      })

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: (data: UpdateReviewCommentMutationResponse) => {
      return queryClient.setQueryData(pullRequestMarkersKey(basePath), (oldMarkersData: Markers) => {
        if (!oldMarkersData) return undefined
        const oldThread = oldMarkersData.threads[data.threadId]
        if (!oldThread) return undefined

        return {
          ...oldMarkersData,
          threads: {
            ...oldMarkersData.threads,
            [data.threadId]: {
              ...oldMarkersData.threads[data.threadId],
              commentsData: {
                ...oldMarkersData.threads[data.threadId]?.commentsData,
                comments: oldMarkersData.threads[data.threadId]?.commentsData.comments.map(comment => {
                  if (comment.databaseId === data.commentDatabaseId) {
                    return {
                      ...comment,
                      body: data.body,
                      bodyHTML: data.bodyHTML,
                    }
                  }
                  return comment
                }),
              },
            },
          },
        }
      })
    },
  })
}
